import { IStorageService } from '../interfaces/IStorageService';
import logger from '../loggerService';
import { obfuscate, deobfuscate } from '../../utils/cryptography';

/**
 * Web Implementation of Storage Service using sessionStorage
 * Enterprise-ready with error handling and robust serialization.
 * 
 * SECURITY: Use this for sensitive data (PII) that should not persist 
 * across browser sessions (Point 5 - Storage Privacy).
 * 
 * MANDATE: All data in sessionStorage is obfuscated to prevent opportunistic reading.
 */
export class WebSessionStorageService implements IStorageService {
    async saveWithExpiry<T>(key: string, value: T, _ttlMs?: number | null): Promise<void> {
        // sessionStorage is session-scoped by nature; TTL is intentionally ignored.
        return this.save(key, value);
    }

    async save<T>(key: string, value: T): Promise<void> {
        try {
            // 1. Serialize the data
            const serializedValue = JSON.stringify(value);
            // 2. Obfuscate the entire JSON string (Point 5 compliance)
            const obfuscatedValue = obfuscate(serializedValue);

            sessionStorage.setItem(key, obfuscatedValue);
        } catch (error) {
            logger.error(`Failed to save to sessionStorage [${key}]`, error, 'WebSessionStorageService');
            throw error;
        }
    }

    async get<T>(key: string): Promise<T | null> {
        try {
            const obfuscatedValue = sessionStorage.getItem(key);
            if (obfuscatedValue === null) return null;

            // 1. De-obfuscate the entire string
            const serializedValue = deobfuscate(obfuscatedValue);
            if (!serializedValue) return null;

            // 2. Parse the JSON back to object
            return JSON.parse(serializedValue) as T;
        } catch (error) {
            logger.warn(`Failed to parse sessionStorage value [${key}]`, { error, context: 'WebSessionStorageService' });
            return null;
        }
    }

    async remove(key: string): Promise<void> {
        try {
            sessionStorage.removeItem(key);
        } catch (error) {
            logger.error(`Failed to remove from sessionStorage [${key}]`, error, 'WebSessionStorageService');
        }
    }

    async clear(): Promise<void> {
        try {
            sessionStorage.clear();
        } catch (error) {
            logger.error('Failed to clear sessionStorage', error, 'WebSessionStorageService');
        }
    }
}
